import type { Activity } from '@fit-editor/fit-core'

export interface ActivityTimeRange {
  start?: Date
  end?: Date
}

/**
 * The activity's start/end instants for display. Prefers the session
 * summary's own `startTime`/`timestamp` (an "original" read) and falls
 * back to the earliest/latest retained record or event timestamp when no
 * session message is present — the same "no session -> raw data range"
 * fallback `computeTiming` uses, kept separate from it here because this
 * is presentation-only and doesn't need `computeTiming`'s pause logic.
 */
export function getActivityTimeRange(activity: Activity): ActivityTimeRange {
  const session = activity.sessions[0]
  if (session?.startTime instanceof Date && session.timestamp instanceof Date) {
    return { start: session.startTime, end: session.timestamp }
  }

  const timestamps: Date[] = []
  for (const record of activity.records) {
    if (record.timestamp instanceof Date) timestamps.push(record.timestamp)
  }
  for (const event of activity.events) {
    if (event.timestamp instanceof Date) timestamps.push(event.timestamp)
  }
  if (timestamps.length === 0) return {}

  const sorted = [...timestamps].sort((a, b) => a.getTime() - b.getTime())
  const start = sorted[0]
  const end = sorted[sorted.length - 1]
  if (start == null || end == null) return {}
  return { start, end }
}

export function getSportLabel(activity: Activity): string {
  const sport = activity.sessions[0]?.sport
  if (typeof sport === 'string' && sport.length > 0) return sport
  return 'Unknown'
}
